import { useEffect, useMemo, useRef, useState } from "react"
import { Link, useLocation } from "react-router-dom"
import { AnimatePresence, motion } from "framer-motion"
import { ChevronDown, Menu, Sparkles, X } from "lucide-react"

import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"

const PRODUCT_LINKS = [
  { label: "Overview", href: "/product" },
  { label: "Core HR", href: "/product/core-hr" },
  { label: "Onboarding & Offboarding", href: "/product/onboarding" },
  { label: "Attendance & Leave", href: "/product/attendance-leave" },
  { label: "Payroll & Compliance", href: "/product/payroll" },
  { label: "Performance Management", href: "/product/performance" },
  { label: "Talent Management", href: "/product/talent-management" },
  { label: "Recruitment / ATS", href: "/product/recruitment" },
  { label: "Learning & Development", href: "/product/learning" },
  { label: "Employee Self-Service", href: "/product/employee-self-service" },
  { label: "AI HR Assistant", href: "/product/ai-assistant" },
  { label: "Analytics & Dashboards", href: "/product/analytics" },
]

const NAV_LINKS = [
  { label: "Solutions", href: "/solutions" },
  { label: "Pricing", href: "/pricing" },
  { label: "Docs", href: "/docs" },
  { label: "Resources", href: "/resources" },
  { label: "About", href: "/about" },
]

export function Header() {
  const [mobileOpen, setMobileOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)
  const [productOpen, setProductOpen] = useState(false)
  const location = useLocation()
  const productWrapRef = useRef<HTMLDivElement | null>(null)
  const productPanelRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8)
    onScroll()
    window.addEventListener("scroll", onScroll, { passive: true })
    return () => window.removeEventListener("scroll", onScroll)
  }, [])

  const isActive = useMemo(() => {
    return (href: string) =>
      href === "/"
        ? location.pathname === "/"
        : location.pathname === href || location.pathname.startsWith(`${href}/`)
  }, [location.pathname])

  const navItemBase =
    "inline-flex items-center h-10 px-[14px] rounded-[21px] text-base font-normal transition-all duration-300"
  const navItemHover = scrolled ? "hover:bg-[#F3F3F7]" : "hover:bg-white"
  const navItemActive = scrolled ? "bg-[#F3F3F7]" : "bg-white"
  const navItemText = "text-[#110718] hover:text-[#110718]"

  const handleProductTriggerLeave: React.MouseEventHandler<HTMLButtonElement> = (e) => {
    const next = e.relatedTarget as Node | null
    if (next && productPanelRef.current?.contains(next)) return
    setProductOpen(false)
  }

  const handleProductPanelLeave: React.MouseEventHandler<HTMLDivElement> = (e) => {
    const next = e.relatedTarget as Node | null
    if (next && productWrapRef.current?.contains(next)) return
    setProductOpen(false)
  }

  return (
    <header
      className={cn(
        "sticky top-0 z-50 w-full transition-[background-color,box-shadow,border-color] duration-200",
        scrolled
          ? "bg-white border-b border-border/70 shadow-[0_1px_7px_rgba(0,0,0,0.08)]"
          : "bg-transparent border-b border-transparent"
      )}
    >
      <div className="container flex h-[70px] items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <div className="relative h-9 w-9 rounded-xl bg-gradient-to-br from-indigo-500 via-blue-500 to-cyan-400 shadow-[0_10px_30px_rgba(37,99,235,0.25)] flex items-center justify-center">
            <span className="text-sm font-bold text-white">N</span>
            <Sparkles className="absolute -top-1 -right-1 h-4 w-4 text-amber-500 bg-white rounded-full p-[2px] shadow-sm ring-1 ring-slate-200" />
          </div>
          <span className="text-sm font-semibold tracking-tight text-[#110718]">Nestor</span>
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          <div ref={productWrapRef} className="relative">
            <button
              className={cn(
                navItemBase,
                navItemHover,
                navItemText,
                "gap-1.5",
                isActive("/product") && navItemActive
              )}
              onMouseEnter={() => setProductOpen(true)}
              onMouseLeave={handleProductTriggerLeave}
              aria-haspopup="menu"
              aria-expanded={productOpen}
            >
              Product
              <ChevronDown
                className={cn(
                  "h-4 w-4 transition-transform duration-300",
                  productOpen ? "rotate-180" : "rotate-0"
                )}
              />
            </button>

            <AnimatePresence>
              {productOpen && (
                <motion.div
                  ref={productPanelRef}
                  role="menu"
                  initial={{ opacity: 0, y: 30 }}
                  animate={{
                    opacity: 1,
                    y: 0,
                    transition: { duration: 0.5, ease: [0.25, 0.1, 0.25, 1] },
                  }}
                  exit={{ opacity: 0, y: 30, transition: { duration: 0.15 } }}
                  onMouseEnter={() => setProductOpen(true)}
                  onMouseLeave={handleProductPanelLeave}
                  className="fixed left-0 right-0 z-[60] top-[70px]"
                >
                  <div
                    className={cn(
                      "mx-auto w-max max-w-[calc(100vw-2rem)]",
                      "rounded-[20px] bg-white p-[30px]",
                      "shadow-[0_15px_30px_rgba(0,0,0,0.14)]"
                    )}
                  >
                    <div className="mb-4 text-sm font-semibold text-[#110718]">
                      Nestor
                    </div>
                    <div className="grid grid-cols-2 gap-2 min-w-[560px] max-w-[720px]">
                      {PRODUCT_LINKS.map((p) => (
                        <Link
                          key={p.href}
                          to={p.href}
                          role="menuitem"
                          onClick={() => setProductOpen(false)}
                          className={cn(
                            "flex items-center justify-between gap-3",
                            "rounded-lg px-3 py-2 text-[15px] text-[#110718]",
                            "transition-colors duration-200 hover:bg-[#F3F3F7]"
                          )}
                        >
                          <span>{p.label}</span>
                          <Sparkles className="h-4 w-4 opacity-30" />
                        </Link>
                      ))}
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {NAV_LINKS.map((l) => (
            <Link
              key={l.href}
              to={l.href}
              className={cn(
                navItemBase,
                navItemHover,
                navItemText,
                isActive(l.href) && navItemActive
              )}
            >
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-2">
          <Button
            variant="ghost"
            className={cn(
              "h-10 rounded-[21px] px-[14px] text-base font-normal",
              navItemText,
              navItemHover
            )}
            asChild
          >
            <Link to="/contact">Talk to Sales</Link>
          </Button>
          <Button className="rounded-full px-6 shadow-[0_10px_30px_rgba(37,99,235,0.22)]" asChild>
            <Link to="/demo">Request Demo</Link>
          </Button>
        </div>

        <button
          className="md:hidden inline-flex h-10 w-10 items-center justify-center rounded-xl border border-border/70 bg-background"
          onClick={() => setMobileOpen((s) => !s)}
          aria-label="Toggle menu"
        >
          {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {mobileOpen && (
        <div className="md:hidden border-b border-border/70 bg-background">
          <div className="container py-4">
            <div className="grid gap-1">
              <div className="px-3 pt-1 pb-2 text-xs font-semibold text-muted-foreground">
                Product
              </div>
              {PRODUCT_LINKS.slice(0, 6).map((l) => (
                <Link
                  key={l.href}
                  to={l.href}
                  onClick={() => setMobileOpen(false)}
                  className={cn(
                    "rounded-xl px-3 py-2 text-sm font-medium text-muted-foreground hover:bg-muted/60 hover:text-foreground",
                    isActive(l.href) && "text-foreground"
                  )}
                >
                  {l.label}
                </Link>
              ))}
              <Link
                to="/product"
                onClick={() => setMobileOpen(false)}
                className="rounded-xl px-3 py-2 text-sm font-medium text-primary hover:bg-muted/60"
              >
                View all modules →
              </Link>

              <div className="mt-2 px-3 pt-3 pb-2 text-xs font-semibold text-muted-foreground">
                Company
              </div>
              {NAV_LINKS.map((l) => (
                <Link
                  key={l.href}
                  to={l.href}
                  onClick={() => setMobileOpen(false)}
                  className={cn(
                    "rounded-xl px-3 py-2 text-sm font-medium text-muted-foreground hover:bg-muted/60 hover:text-foreground",
                    isActive(l.href) && "text-foreground"
                  )}
                >
                  {l.label}
                </Link>
              ))}
            </div>
            <div className="mt-4 grid gap-2">
              <Button variant="outline" asChild>
                <Link to="/contact" onClick={() => setMobileOpen(false)}>
                  Talk to Sales
                </Link>
              </Button>
              <Button asChild>
                <Link to="/demo" onClick={() => setMobileOpen(false)}>
                  Request Demo
                </Link>
              </Button>
            </div>
          </div>
        </div>
      )}
    </header>
  )
}

